import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Radio, Row } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorResult, SketchPicker } from 'react-color';
import { isEmpty } from 'lodash';
import { PRODUCT_TILE } from '../../../../constants/define/translation';
import {
  postCreateTileWidgetRepository,
  patchUpdateTileWidgetRepository,
} from '../../../../repository/mytm/shop/shopRepository';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { SHOP_TYPE, ShopStatus, ShopType } from '../../../../types/shop';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SelectOption, SpinLoading } from '../../../atoms';
import { PreviewProductTile } from '../../../molecules/preview/common';
import { DragDropImageVideo } from '../../../molecules/upload';
import { CountryContext } from '../../../../repository/context/countryContext';
import { getFuLLUrlEcom, URLType, URLTypeList } from '../../../../constants/common';

interface FormProductTileProps {
  record?: ShopType;
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
  countrySelected: string;
}

const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;
const { TextArea } = Input;

export const FormProductTile = (props: FormProductTileProps) => {
  const { t } = useTranslation(PRODUCT_TILE);
  const { record, onRefresh, onCloseModal, setDataRecord, countrySelected } = props;
  const [form] = Form.useForm();
  const options = record?.options ? JSON.parse(record.options) : null;
  const defaultValue = {
    title: record?.title,
    subTitle: record?.description,
    type: SHOP_TYPE.PRODUCT_TILES,
    imageLink: record?.imageLink,
    videoLink: record?.videoLink,
    ctaText: record?.ctaText || 'Shop Now',
    ctaLink: record?.ctaLink || '',
    status: record?.status || ShopStatus.INACTIVE,
    countries: record ? record?.countries : countrySelected || 'USA',
    titleColor: options?.titleColor || '#000',
    subColor: options?.subColor || '#000',
    background: options?.background || '#fff',
    ctaTextColor: options?.ctaTextColor || '#fff',
    ctaBackgroundColor: options?.ctaBackgroundColor || '#000',
    ctaLinkType: options?.ctaLinkType || URLType.PDP,
  };
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [isBlockCtaLink, setBlockCtaLink] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [videoList, setVideoList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState<any | undefined>(defaultValue);
  const countryContext = useContext(CountryContext);
  const [isShowColorPicker, setIsShowColorPicker] = useState<any>({
    isBackGroundColor: false,
    isTitleColor: false,
    isSubColor: false,
    isCTATextColor: false,
    isCTABackgroundColor: false,
  });

  useEffect(() => {
    if (imageList.length || videoList.length) {
      setMessageErrorUploadImage('');
    }
  }, [imageList, videoList]);

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSetVideoList = useCallback((data: UploadFileToBlobInterface[]) => {
    setVideoList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onChangeInput = useCallback(
    e => {
      const newField = form.getFieldsValue();
      let ctaNewLink = newField?.ctaLink || '';
      if (
        newField &&
        newField.ctaLinkType &&
        ![URLType.PDP, URLType.CUSTOM, URLType.GRID_PAGE].includes(newField.ctaLinkType)
      ) {
        const dataUrl = getFuLLUrlEcom(newField?.ctaLinkType, newField?.countries);
        ctaNewLink = dataUrl?.ctaNewLink;
        setBlockCtaLink(dataUrl?.blockLink);

        form.setFieldsValue({
          ...newField,
          ctaLink: ctaNewLink,
        });
      } else {
        setBlockCtaLink(false);
      }
      setDataForm((old: any) => ({
        ...old,
        title: newField?.title,
        subTitle: newField?.subTitle,
        imageLink: newField?.imageLink,
        videoLink: newField?.videoLink,
        ctaText: newField?.ctaText,
        ctaLink: ctaNewLink,
        ctaLinkType: newField?.ctaLinkType,
      }));
    },
    [form],
  );

  const onShowColorPicker = useCallback((fieldName: string, value) => {
    setIsShowColorPicker({
      ...isShowColorPicker,
      [fieldName]: value,
    });
  }, []);

  const onCloseColorPicker = useCallback(
    (fieldName: string) => {
      setIsShowColorPicker({
        ...isShowColorPicker,
        [fieldName]: false,
      });
    },
    [isShowColorPicker],
  );

  const handleChangeColor = useCallback(
    (value: ColorResult, fieldName: string) => {
      setDataForm((old: any) => ({
        ...old,
        [fieldName]: value?.hex,
      }));
    },
    [dataForm],
  );

  const onFinish = useCallback(
    (formValue: any) => {
      if (!(imageList.length || videoList.length)) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }
      setCreateLoading(true);
      let imgList = imageList.filter(img => !img.isImageURL);
      let imgLink = imageList.filter(img => img.isImageURL);
      if (isEmpty(imgList)) {
        imgList = videoList.filter(img => !img.isVideoURL);
      }

      if (isEmpty(imgLink)) {
        imgLink = videoList.filter(img => img.isVideoURL);
      }

      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const videoType = !!videoList[0]?.fileType?.includes('video');
          const url = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];

          const countryStr = form.getFieldValue('countries').toString();
          const newOptions = {
            titleColor: dataForm?.titleColor,
            subColor: dataForm?.subColor,
            background: dataForm?.background,
            ctaTextColor: dataForm?.ctaTextColor,
            ctaBackgroundColor: dataForm?.ctaBackgroundColor,
            ctaLinkType: dataForm?.ctaLinkType,
          };
          const payloadToPost = {
            title: dataForm?.title,
            description: dataForm?.subTitle,
            type: SHOP_TYPE.PRODUCT_TILES,
            imageLink: (!videoType && url) || null,
            videoLink: (videoType && url) || null,
            ctaText: dataForm?.ctaText,
            ctaLink: dataForm?.ctaLink,
            options: newOptions,
            countries: countryStr,
            status: dataForm?.status,
          };
          if (record) {
            return patchUpdateTileWidgetRepository({
              ...payloadToPost,
              status: record?.status,
              id: record?.id,
            })
              .then(() => {
                onRefresh();
                onCloseModal();
                message.success(t('message.success.updateProductTile'));
              })
              .catch(e => {
                setCreateLoading(true);
                message.error(e?.errorMessage);
              })
              .finally(() => {
                setCreateLoading(false);
              });
          }
          return postCreateTileWidgetRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.success.createProductTile'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
          setDataRecord(null);
        });
    },
    [record, imageList, dataForm, videoList],
  );

  useEffect(() => {
    if (record) {
      setImageList(record?.imageLink ? [{ dataURL: record?.imageLink, isImageURL: true }] : []);
      setVideoList(
        record?.videoLink ? [{ dataURL: record?.videoLink || null, isVideoURL: true, fileType: 'video' }] : [],
      );
      setSelectedImage(0);
      setDataForm({
        ...defaultValue,
        countries: record?.countries,
      });
      if (options && options.ctaLinkType) {
        if (
          options.ctaLinkType === URLType.PDP ||
          options.ctaLinkType === URLType.CUSTOM ||
          options.ctaLinkType === URLType.GRID_PAGE
        ) {
          setBlockCtaLink(false);
        } else {
          setBlockCtaLink(true);
        }
      }
    }
  }, [record]);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-ProductTile">
            <Row className="form-productTile-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImageVideo
                  {...{
                    imageList,
                    onSetImageList,
                    setMessageErrorUploadImage,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    videoList,
                    onSetVideoList,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewProductTile
                        values={{
                          ...dataForm,
                          backgroundImage: (imageList?.length && imageList[0].dataURL) || null,
                          backgroundVideo: (videoList?.length && videoList[0].dataURL) || null,
                          describeText: t('form.imageUpload'),
                          describeText2: t('form.image3x4'),
                        }}
                        className="preview-product-tile"
                        height="410px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormProductTile span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="countries"
                        label={t('form.countries')}
                        rules={[{ required: true, message: t('validate.countryBlank') }]}
                      >
                        <Radio.Group options={countryOptions} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="title"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="subTitle" label={t('form.subTitle')}>
                        <TextArea
                          style={{ overflow: 'hidden' }}
                          rows={3}
                          placeholder={t('form.subTitle')}
                          onChange={onChangeInput}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <SelectOption
                        modeForm
                        name="ctaLinkType"
                        label={t('form.linkType')}
                        rules={[{ required: true, message: t('validate.linkTypeBlank') }]}
                        values={URLTypeList}
                        textDisplay="text"
                        onChange={onChangeInput}
                        showSearch
                        allowClear
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <Form.Item
                        name="ctaLink"
                        label={t('form.link')}
                        rules={[{ required: true, message: t('validate.linkBlank') }]}
                      >
                        <Input
                          disabled={isBlockCtaLink}
                          placeholder={t('form.link')}
                          maxLength={255}
                          onChange={onChangeInput}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[12, 12]}>
                    <Col span={8}>
                      <Form.Item label={t('form.backgroundColor')} name="background">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isBackGroundColor', !isShowColorPicker.isBackGroundColor);
                          }}
                        />
                        {isShowColorPicker.isBackGroundColor && (
                          <div className="popover">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isBackGroundColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isBackGroundColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.background}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'background');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={t('form.titleColor')} name="titleColor">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isTitleColor', !isShowColorPicker.isTitleColor);
                          }}
                        />
                        {isShowColorPicker.isTitleColor && (
                          <div className="popover">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isTitleColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isTitleColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.titleColor}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'titleColor');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={t('form.subColor')} name="subColor">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isSubColor', !isShowColorPicker.isSubColor);
                          }}
                        />
                        {isShowColorPicker.isSubColor && (
                          <div className="popoverSub">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isSubColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isSubColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.subColor}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'subColor');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[12, 12]}>
                    <Col span={8}>
                      <Form.Item
                        name="ctaText"
                        label={t('form.cta')}
                        rules={[{ required: true, message: t('validate.ctaBlank') }]}
                      >
                        <Input placeholder={t('form.cta')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={t('form.ctaTextColor')} name="ctaTextColor">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isCTATextColor', !isShowColorPicker.isCTATextColor);
                          }}
                        />
                        {isShowColorPicker.isCTATextColor && (
                          <div className="popover">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isCTATextColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isCTATextColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.ctaTextColor}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'ctaTextColor');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item label={t('form.ctaBackgroundColor')} name="ctaBackgroundColor">
                        <ButtonActionPage
                          text="Pick"
                          id="1"
                          className="btn-pick-color"
                          onClick={() => {
                            onShowColorPicker('isCTABackgroundColor', !isShowColorPicker.isCTABackgroundColor);
                          }}
                        />
                        {isShowColorPicker.isCTABackgroundColor && (
                          <div className="popoverSub">
                            <div
                              className="cover"
                              onClick={() => {
                                onCloseColorPicker('isCTABackgroundColor');
                              }}
                              onKeyDown={() => {
                                onCloseColorPicker('isCTABackgroundColor');
                              }}
                              role="button"
                              tabIndex={0}
                            >
                              {' '}
                            </div>
                            <SketchPicker
                              color={dataForm?.ctaBackgroundColor}
                              onChange={colorPick => {
                                handleChangeColor(colorPick, 'ctaBackgroundColor');
                              }}
                            />
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end' }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdateProductTile') : t('form.btnCreateProductTile')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormProductTile>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-ProductTile-container {
    padding: 1.5rem;

    .form-ProductTile__description {
      resize: none;
    }
  }

  & .preview-product-tile {
    border-radius: 18px;
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
    bottom: -15px !important;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background-color: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormProductTile = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }

  & .popover {
    position: absolute;
    z-index: 2;
    bottom: 0;
    left: 10%;
  }

  & .popoverSub {
    position: absolute;
    z-index: 2;
    bottom: 0;
    right: 0;
  }

  & .cover {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  & .sketch-picker {
    cursor: pointer;
  }
`;
